import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import ClabLogoPng from 'components/ui/ClabLogoPng';
import { useAuth } from '../../../context/AuthContext';

const LoginForm = ({ selectedLanguage = 'en', mode = 'regular', theme = 'dark' }) => {
  const navigate = useNavigate();
  const { signIn, eclabSignIn, loading, error } = useAuth();
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const isEclabMode = mode === 'eclab';
  const isRecruiterMode = mode === 'recruiter';

  // Theme configuration
  const isDarkTheme = theme === 'dark' || theme === 'recruiter';
  const isRecruiterTheme = theme === 'recruiter';
  const labelClass = isDarkTheme ? "text-white" : "text-gray-700";
  const checkboxTextClass = isDarkTheme ? "text-white" : "text-gray-700";
  const buttonClass = isRecruiterTheme
    ? "bg-white text-[#F47B20] hover:bg-gray-100 focus:ring-white font-bold"
    : isDarkTheme
      ? "bg-white text-primary-700 hover:bg-gray-100 focus:ring-white"
      : "bg-[#00939d] text-white hover:bg-[#007f88] focus:ring-[#00939d]";
  const linkClass = isDarkTheme ? "text-white hover:text-blue-100" : "text-[#00939d] hover:text-[#007f88]";
  const iconClass = "text-gray-400"; // Always gray/neutral inside inputs

  // Demo credentials for different roles
  // ... (keep demo credentials logic) ...

  const demoCredentials = [
    { role: 'Master Admin', username: 'master', password: 'master123' },
    { role: 'CLAB User', username: 'admin', password: 'admin123' },
    { role: 'Recruiter', username: 'recruiter', password: 'password123' },
  ];

  // E-CLAB demo credentials (actual test accounts provided)
  const eclabDemoCredentials = [
    { role: 'SPE ELECTRICAL ENGINEERING', username: 'CLAB004143', password: 'Abcd@1234' },
    { role: 'NORTHERN SUN STEELWORKS', username: 'CLAB003798', password: 'Abcd@1234' },
    { role: 'KECK SENG RENOVATION', username: 'CLAB003734', password: 'Abcd@1234' },
    { role: 'EAST COAST INDUSTRIAL', username: 'CLAB005590', password: 'Abcd@1234' },
    { role: 'BUCIDA ENGINEERING', username: 'CLAB004093', password: 'Abcd@1234' },
    { role: 'CHEN GUAN AIR-CONDITIONING & ENGINEERING SDN. BHD.', username: 'CLAB002167', password: 'Abcd@1234' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);

    try {
      let data, error;
      
      if (isEclabMode) {
        // Use E-CLAB login
        const result = await eclabSignIn({ username: usernameOrEmail, password });
        data = result.data;
        error = result.error;
      } else {
        // Use regular login
        const result = await signIn({ username: usernameOrEmail, password });
        data = result.data;
        error = result.error;
      }
      
      if (error) {
        setLoginError(error.message);
      } else if (data?.user) {
        // --- Debug Role Logic ---

        // Normalize Role
        let role = 'guest';
        const rawRole = data.user.role_name || data.user.role;
        
        if (typeof rawRole === 'string') {
          role = rawRole.toLowerCase();
        } else if (typeof rawRole === 'number') {
           // Fallback mapping if frontend didn't map it yet
           const map = { 0: 'admin', 1: 'clab', 2: 'recruiter', 3: 'employer' };
           role = map[rawRole] || 'guest';
        }
        
        // Handle variations
        if (role === 'clab_user' || role === 'clab user') role = 'clab';
        if (role === 'master_admin' || role === 'master admin') role = 'admin';
        
        // Strict Role-Based Access Control
        const isEclabUser = isEclabMode || data.user.eclabUser; // Employer Login
        
        // Define allowed roles for each mode
        let isAllowed = false;
        
        if (mode === 'eclab') {
            // Only Employers allowed on Employer Login
            if (isEclabUser || role === 'employer') isAllowed = true;
        } else if (mode === 'recruiter') {
            // Only Recruiters allowed on Recruiter Login
            if (role === 'recruiter') isAllowed = true;
        } else if (mode === 'regular') {
            // Only Admin/CLAB allowed on Main Login
            // Explicitly disallow recruiters and employers
            const clabRoles = ['admin', 'master_admin', 'clab', 'clab_user'];
            // Also allow 'guest' if we assume guest is just failed role, but better to be strict
            if (clabRoles.includes(role)) isAllowed = true;
        }

        if (!isAllowed) {
             console.warn(`Access Denied: Role '${role}' not allowed in mode '${mode}'`);
             setLoginError(`Access Denied: You are not authorized to login here.`);
             // Force logout to clear the invalid session immediately
             try {
                if(signOut) signOut(); 
                else {
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                }
             } catch(e) { console.error(e); }
             return;
        }
        
        // Proceed with navigation if allowed
        if (isEclabUser) {
          navigate('/employer-application-management');
        } else {
          switch (role) {
            case 'admin':
            case 'master_admin':
              navigate('/master-admin-dashboard');
              break;
            case 'clab':
            case 'clab_user':
              navigate('/clab-dashboard');
              break;
            case 'recruiter':
              navigate('/recruiter-biodata-management'); // Redirect to recruiter dashboard
              break;
            case 'employer':
              navigate('/employer-biodata-selection');
              break;
            default:
              navigate('/clab-dashboard');
          }
        }
      } else {
        setLoginError('Login failed. No user data received.');
      }
    } catch (err) {
      console.error(err);
      setLoginError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleDemoLogin = (credentials) => {
    setUsernameOrEmail(credentials.username);
    setPassword(credentials.password);
  };

  const getTranslatedText = (key) => {
    const translations = {
      en: {
        title: 'Welcome to eBiodata CLAB',
        subtitle: 'Construction Labour Exchange Centre Berhad',
        usernameOrEmail: 'Username or Email',
        password: 'Password',
        rememberMe: 'Remember me',
        forgotPassword: 'Forgot your password?',
        signIn: 'Sign In',
        demoTitle: 'Demo Login Credentials',
        demoSubtitle: 'Click any role to auto-fill login credentials',
        usernamePlaceholder: 'Enter your username or email',
        passwordPlaceholder: 'Enter your password'
      },
      ar: {
        title: 'مرحباً بك في نظام eBiodata CLAB',
        subtitle: 'نظام المكتب المركزي لإدارة العمالة',
        usernameOrEmail: 'اسم المستخدم أو البريد الإلكتروني',
        password: 'كلمة المرور',
        rememberMe: 'تذكرني',
        forgotPassword: 'نسيت كلمة المرور؟',
        signIn: 'تسجيل الدخول',
        demoTitle: 'بيانات تسجيل الدخول التجريبية',
        demoSubtitle: 'انقر على أي دور لملء بيانات تسجيل الدخول تلقائياً',
        usernamePlaceholder: 'أدخل اسم المستخدم أو البريد الإلكتروني',
        passwordPlaceholder: 'أدخل كلمة المرور'
      }
    };

    const baseText = translations[selectedLanguage]?.[key] || translations.en[key];

    if (isRecruiterMode) {
      const recruiterOverrides = {
        usernameOrEmail: 'e-CLAB Recruiter ID',
        password: 'Enter Password',
        usernamePlaceholder: '',
        passwordPlaceholder: '',
        demoTitle: 'Recruiter Test Accounts',
        demoSubtitle: 'Click to auto-fill recruiter credentials'
      };
      return recruiterOverrides[key] || baseText;
    }

    if (!isEclabMode) {
      return baseText;
    }

    const eclabOverrides = {
      title: 'E-CLAB Employer Login',
      subtitle: 'Use your E-CLAB employer credentials to continue',
      demoTitle: 'E-CLAB Test Accounts',
      demoSubtitle: 'Click any company below to auto-fill E-CLAB credentials'
    };

    return eclabOverrides[key] || baseText;
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      {/* Header removed - branding handled by parent pages */}

      {/* Demo Credentials - Only show in development/UAT environment */}
      {(import.meta.env.VITE_SHOW_DEMO_CREDENTIALS && import.meta.env.VITE_SHOW_DEMO_CREDENTIALS !== 'false') && (
        <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
          <h3 className="text-sm font-medium text-primary-700 mb-2">
            {getTranslatedText('demoTitle')}
          </h3>
          <p className="text-xs text-primary-600 mb-3">
            {getTranslatedText('demoSubtitle')}
          </p>
          <div className="grid grid-cols-1 gap-2">
            {(isEclabMode ? eclabDemoCredentials : demoCredentials).map((cred, index) => (
              <button
                key={index}
                onClick={() => handleDemoLogin(cred)}
                className="text-left p-2 text-xs bg-white border border-primary-200 rounded hover:bg-primary-100 transition-colors duration-150"
              >
                <div className="font-medium text-primary-700">{cred.role}</div>
                <div className="text-primary-600">{cred.username}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {(loginError || error) && (
        <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={16} className="text-error mr-2" />
            <span className="text-sm text-error">{loginError || error}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Username or Email Field */}
        <div>
          <label htmlFor="usernameOrEmail" className={`block text-sm font-medium mb-2 ${labelClass}`}>
            {getTranslatedText('usernameOrEmail')}
          </label>
          <div className="relative">
            <Icon 
              name="User" 
              size={20} 
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconClass}`} 
            />
            <input
              id="usernameOrEmail"
              type="text"
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-150"
              placeholder={getTranslatedText('usernamePlaceholder') || "Enter your username or email"}
              required
            />
          </div>
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className={`block text-sm font-medium mb-2 ${labelClass}`}>
            {getTranslatedText('password')}
          </label>
          <div className="relative">
            <Icon 
              name="Lock" 
              size={20} 
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${iconClass}`}
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-150"
              placeholder={getTranslatedText('passwordPlaceholder') || "Enter your password"}
              required
            />
          </div>
        </div>

        {/* Show Password & Remember Me */}
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              className="rounded border-secondary-300 text-primary focus:ring-primary-500 w-4 h-4"
            />
            <span className={`ml-2 ${checkboxTextClass}`}>
              Show Password
            </span>
          </label>

          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="rounded border-secondary-300 text-primary focus:ring-primary-500 w-4 h-4"
            />
            <span className={`ml-2 ${checkboxTextClass}`}>
              {getTranslatedText('rememberMe')}
            </span>
          </label>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoggingIn || loading}
          className={`w-full py-3 px-4 rounded-lg font-bold focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide shadow-md ${buttonClass}`}
        >
          {isLoggingIn || loading ? (
            <div className="flex items-center justify-center">
              <Icon name="Loader" size={20} className="animate-spin mr-2" />
              Signing In...
            </div>
          ) : (
            "LOGIN"
          )}
        </button>
      </form>

      {/* Footer removed - handled by parent pages */}
    </div>
  );
};

export default LoginForm;
